import React from "react"

/**
 * Structural subset of the Novu in-app notification used by the custom
 * renderer. Kept local so we don't couple to a deep @novu/js import path.
 * `data` carries the trigger payload IF the workflow's in-app step maps it
 * (Subject/Body/Redirect can be set from {{payload.*}} and a Data object with
 * status / total / successful / failed / progress / kind / source / jobId).
 */
export interface NovuNotificationLike {
  subject?: string
  body?: string
  data?: Record<string, unknown>
  redirect?: { url?: string }
  isRead?: boolean
  createdAt?: string
  read?: () => void
}

type JobStatus = "started" | "in_progress" | "success" | "partial" | "failed"

const STATUS_META: Record<
  JobStatus,
  {
    label: string
    dot: string
    bar: string
    ring: string
    icon: React.ReactNode
  }
> = {
  started: {
    label: "Started",
    dot: "bg-sky-400",
    bar: "bg-sky-400",
    ring: "bg-sky-500/20 text-sky-300",
    icon: "↗",
  },
  in_progress: {
    label: "In progress",
    dot: "bg-amber-400",
    bar: "bg-amber-400",
    ring: "bg-amber-500/20 text-amber-300",
    icon: "⏳",
  },
  success: {
    label: "Done",
    dot: "bg-emerald-400",
    bar: "bg-emerald-400",
    ring: "bg-emerald-500/20 text-emerald-300",
    icon: "✓",
  },
  partial: {
    label: "Partial",
    dot: "bg-amber-400",
    bar: "bg-amber-400",
    ring: "bg-amber-500/20 text-amber-300",
    icon: "!",
  },
  failed: {
    label: "Failed",
    dot: "bg-rose-400",
    bar: "bg-rose-500",
    ring: "bg-rose-500/20 text-rose-300",
    icon: "✕",
  },
}

function num(v: unknown): number | undefined {
  if (typeof v === "number") return Number.isFinite(v) ? v : undefined
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v)
    return Number.isFinite(n) ? n : undefined
  }
  return undefined
}

/** "3/8" → 0..1 fraction for the progress bar. */
function progressFraction(
  progress: unknown,
  total?: number,
  done?: number,
): number | undefined {
  if (typeof progress === "string" && progress.includes("/")) {
    const [a, b] = progress.split("/").map(s => Number(s.trim()))
    if (Number.isFinite(a) && Number.isFinite(b) && b > 0) {
      return Math.min(1, a / b)
    }
  }
  if (total && total > 0 && done !== undefined) return Math.min(1, done / total)
  return undefined
}

function relativeTime(iso?: string): string {
  if (!iso) return ""
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ""
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000))
  if (secs < 60) return "just now"
  const mins = Math.round(secs / 60)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.round(hrs / 24)}d ago`
}

/** Best-effort status when the workflow hasn't mapped `data.status` yet. */
function inferStatus(subject?: string, body?: string): JobStatus {
  const t = `${subject ?? ""} ${body ?? ""}`.toLowerCase()
  if (t.includes("fail")) return "failed"
  if (t.includes("partial")) return "partial"
  if (t.includes("complete") || t.includes("done") || t.includes("created")) {
    return "success"
  }
  if (t.includes("in progress")) return "in_progress"
  return "started"
}

export function JobNotificationCard({
  notification,
  onNavigate,
}: {
  notification: NovuNotificationLike
  onNavigate: (url: string) => void
}) {
  const data = notification.data ?? {}
  const status = ((data.status as string) ||
    inferStatus(notification.subject, notification.body)) as JobStatus
  const meta = STATUS_META[status] ?? STATUS_META.started

  const total = num(data.total)
  const successful = num(data.successful)
  const failed = num(data.failed)
  const fraction = progressFraction(
    data.progress,
    total,
    status === "success" || status === "partial" ? total : undefined,
  )
  const showBar = status === "started" || status === "in_progress"

  // Job notifications always land on the Jobs page. Prefer a job-specific deep
  // link (from the payload), else honour an explicit redirect ONLY if it already
  // points at /jobs (a correctly-configured workflow) — never a stale
  // /ad-blaster redirect — else fall back to the Jobs list.
  const jobId = data.jobId ? String(data.jobId) : undefined
  const dataRedirect =
    typeof data.redirectUrl === "string" ? data.redirectUrl : undefined
  const workflowRedirect = notification.redirect?.url
  const target =
    dataRedirect ||
    (jobId ? `/jobs?job=${jobId}` : undefined) ||
    (workflowRedirect && workflowRedirect.startsWith("/jobs")
      ? workflowRedirect
      : undefined) ||
    "/jobs"

  const handleClick = () => {
    notification.read?.()
    onNavigate(target)
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") handleClick()
      }}
      className="flex gap-3 px-3 py-3 cursor-pointer hover:bg-white/5 transition-colors"
    >
      <div
        className={`mt-0.5 h-7 w-7 shrink-0 rounded-full flex items-center justify-center text-sm font-semibold ${meta.ring}`}
      >
        {meta.icon}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-system-gray-100">
            {notification.subject || "Ad blast"}
          </span>
          <span
            className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${meta.ring}`}
          >
            {meta.label}
          </span>
          {!notification.isRead && (
            <span
              className={`ml-auto h-2 w-2 shrink-0 rounded-full ${meta.dot}`}
            />
          )}
        </div>

        {notification.body && (
          <p className="mt-0.5 truncate text-xs text-system-gray-400">
            {notification.body}
          </p>
        )}

        {showBar && fraction !== undefined && (
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
            <div
              className={`h-full rounded-full ${meta.bar} transition-all`}
              style={{ width: `${Math.round(fraction * 100)}%` }}
            />
          </div>
        )}

        <div className="mt-1.5 flex items-center gap-3 text-[11px] text-system-gray-500">
          {total !== undefined &&
            (successful !== undefined || failed !== undefined) && (
              <span>
                {successful !== undefined && (
                  <span className="text-emerald-400">{successful} ok</span>
                )}
                {failed ? (
                  <span className="text-rose-400"> · {failed} failed</span>
                ) : null}
                <span> · {total} total</span>
              </span>
            )}
          {typeof data.progress === "string" && showBar && (
            <span>{data.progress}</span>
          )}
          <span className="ml-auto">
            {relativeTime(notification.createdAt)}
          </span>
        </div>
      </div>
    </div>
  )
}
